import type { ContextBlock, PipelineStage } from '../types';
import { draftText } from '../draft-text';
import { parseErrorLocations, matchesPath } from '../../parsing/error-locations';

export const b3ErrorFocusedSlicing: PipelineStage = {
  id: 'B3',
  group: 'B',
  label: 'Error-focused context slicing',
  description: 'When the prompt contains a stack trace, include only the referenced lines plus surrounding context.',
  impact: 'Medium',
  defaultEnabled: true,
  implemented: true,
  params: {
    contextLines: { type: 'number', default: 8, min: 0, max: 100, label: 'Surrounding lines (N)' },
  },

  async run(draft, params, ctx) {
    const tokensBefore = ctx.estimateTokens(draftText(draft));
    const locations = parseErrorLocations(draft.userMessage);

    if (locations.length === 0) {
      return {
        draft,
        report: { stageId: 'B3', tokensBefore, tokensAfter: tokensBefore, tokensSaved: 0, notes: ['No stack trace/error location detected in the message.'] },
      };
    }

    const contextLines = Number(params.contextLines) || 8;
    let slicedCount = 0;

    const newBlocks: ContextBlock[] = draft.contextBlocks.map((block) => {
      if (!block.id.startsWith('file:')) return block;
      const path = block.id.slice('file:'.length);
      const match = locations.find((loc) => matchesPath(path, loc.path));
      if (!match) return block;

      const lines = block.content.split('\n');
      const start = Math.max(0, match.line - 1 - contextLines);
      const end = Math.min(lines.length, match.line + contextLines);
      slicedCount++;

      return {
        ...block,
        label: `${path} (lines ${start + 1}-${end}, around the error at line ${match.line})`,
        content: lines.slice(start, end).join('\n'),
        // Slicing rebases every offset — B1's spans don't describe this
        // content any more, so drop them instead of misleading B4.
        spans: undefined,
      };
    });

    if (slicedCount === 0) {
      return {
        draft,
        report: {
          stageId: 'B3',
          tokensBefore,
          tokensAfter: tokensBefore,
          tokensSaved: 0,
          notes: ['Error location(s) detected but none matched a currently open file.'],
        },
      };
    }

    const newDraft = { ...draft, contextBlocks: newBlocks };
    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    return {
      draft: newDraft,
      report: {
        stageId: 'B3',
        tokensBefore,
        tokensAfter,
        tokensSaved: Math.max(0, tokensBefore - tokensAfter),
        notes: [`Sliced ${slicedCount} file block(s) down to ±${contextLines} lines around the detected error location.`],
      },
    };
  },
};
